import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DollarSign, TrendingUp, AlertTriangle, Users, FileText, RefreshCw, ChevronRight, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';

const UTIL_COLORS = {
  OPTIMAL: 'text-emerald-400',
  MODERATE: 'text-yellow-400',
  UNDER_UTILISED: 'text-orange-400',
  UNBILLED: 'text-red-400',
  IDLE: 'text-slate-500',
  LOW: 'text-red-400',
};

const SEVERITY_BG = {
  CRITICAL: 'bg-red-600 text-white',
  HIGH: 'bg-orange-500 text-white',
  MEDIUM: 'bg-yellow-500 text-black',
  LOW: 'bg-slate-500 text-white',
};

export default function RevenueOptimizationPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [funding, setFunding] = useState(null);
  const [missed, setMissed] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ov, fu, mi, an] = await Promise.all([
        api.get('/revenue/overview'),
        api.get('/revenue/funding-utilisation'),
        api.get('/revenue/missed-billing'),
        api.get('/revenue/anomalies'),
      ]);
      if (ov.data?.data) setOverview(ov.data.data);
      if (fu.data?.data) setFunding(fu.data.data);
      if (mi.data?.data) setMissed(mi.data.data);
      if (an.data?.data) setAnomalies(an.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading) return <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'funding', label: 'Funding', icon: Users },
    { id: 'missed', label: 'Missed Billing', icon: FileText },
    { id: 'anomalies', label: 'Anomalies', icon: AlertTriangle },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="revenue-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><DollarSign className="h-6 w-6 text-emerald-400" />Revenue Optimization</h1>
            <p className="text-sm text-slate-400 mt-1">NDIS funding insights and missed billing detection</p>
          </div>
          <Button onClick={fetchAll} className="bg-blue-600 hover:bg-blue-700" data-testid="refresh-revenue-btn"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-[#12121a] border-[#1e1e2e]" data-testid="capture-rate-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Capture Rate</p>
              <p className={`text-3xl font-bold mt-1 ${(overview?.capture_rate || 0) >= 80 ? 'text-emerald-400' : 'text-orange-400'}`}>{overview?.capture_rate || 0}%</p>
            </CardContent>
          </Card>
          <Card className="bg-[#12121a] border-[#1e1e2e]" data-testid="leakage-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Revenue Leakage</p>
              <p className="text-3xl font-bold mt-1 text-red-400">{overview?.leakage_count || 0}</p>
              <p className="text-[10px] text-slate-600">{overview?.leakage_rate || 0}% of links</p>
            </CardContent>
          </Card>
          <Card className="bg-[#12121a] border-[#1e1e2e]" data-testid="invoiced-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Invoiced Links</p>
              <p className="text-3xl font-bold mt-1 text-emerald-400">{overview?.invoiced_links || 0}</p>
              <p className="text-[10px] text-slate-600">of {overview?.total_service_links || 0} total</p>
            </CardContent>
          </Card>
          <Card className="bg-[#12121a] border-[#1e1e2e]" data-testid="missed-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Missed Opportunities</p>
              <p className="text-3xl font-bold mt-1 text-amber-400">{missed?.total_missed_opportunities || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#1e1e2e] pb-0">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm flex items-center gap-1.5 border-b-2 transition-colors ${activeTab === t.id ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && overview && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-[#12121a] border-[#1e1e2e]"><CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Billing Pipeline</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {[
                  { label: 'Billable Sessions', value: overview.billable_sessions, color: 'text-blue-400' },
                  { label: 'Participant Slots', value: overview.total_participant_slots, color: 'text-blue-400' },
                  { label: 'Invoiced', value: overview.invoiced_links, color: 'text-emerald-400' },
                  { label: 'Ready (Not Invoiced)', value: overview.ready_not_invoiced, color: 'text-amber-400' },
                  { label: 'Pending Review', value: overview.pending_review, color: 'text-yellow-400' },
                  { label: 'Manual Review', value: overview.manual_review, color: 'text-red-400' },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between text-sm"><span className="text-slate-500">{row.label}</span><span className={`font-mono ${row.color}`}>{row.value}</span></div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-[#12121a] border-[#1e1e2e]"><CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Revenue Health</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div><p className="text-xs text-slate-500">Capture Rate</p>
                  <div className="h-3 bg-[#1a1a2e] rounded-full mt-1 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${overview.capture_rate}%` }} /></div>
                  <p className="text-xs text-slate-500 mt-0.5">{overview.capture_rate}% of service links invoiced</p>
                </div>
                <div><p className="text-xs text-slate-500">Leakage Rate</p>
                  <div className="h-3 bg-[#1a1a2e] rounded-full mt-1 overflow-hidden"><div className="h-full bg-red-500 rounded-full" style={{ width: `${overview.leakage_rate}%` }} /></div>
                  <p className="text-xs text-slate-500 mt-0.5">{overview.leakage_rate}% potential revenue not captured</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'funding' && funding && (
          <Card className="bg-[#12121a] border-[#1e1e2e]">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base text-slate-300">Participant Funding ({funding.summary?.total || 0})</CardTitle>
              <div className="flex gap-2 text-[10px]">
                {Object.entries(funding.summary || {}).filter(([k]) => k !== 'total').map(([k, v]) => (
                  <span key={k} className="px-2 py-0.5 rounded bg-[#0d0d14] text-slate-500">{k.replace('_', ' ')}: {v}</span>
                ))}
              </div>
            </CardHeader>
            <CardContent className="p-0"><div className="divide-y divide-[#1e1e2e]">
              {(funding.participants || []).map((p, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5" data-testid={`funding-row-${i}`}>
                  <span className={`text-lg font-bold w-12 text-right ${UTIL_COLORS[p.utilisation_status]}`}>{p.utilisation_pct}%</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200 truncate">{p.name}</p>
                    <p className="text-[10px] text-slate-600">{p.ndis_number} | {p.sessions} sessions | {p.invoiced}/{p.total_links} invoiced</p>
                  </div>
                  <Badge className={`text-[10px] px-1.5 ${p.utilisation_status === 'OPTIMAL' ? 'bg-emerald-900/50 text-emerald-300' : p.utilisation_status === 'IDLE' ? 'bg-slate-800 text-slate-500' : 'bg-orange-900/50 text-orange-300'}`}>{p.utilisation_status}</Badge>
                  {p.plan_expiring_soon && <Badge className="bg-red-900/50 text-red-300 text-[10px]">Plan Expiring</Badge>}
                </div>
              ))}
            </div></CardContent>
          </Card>
        )}

        {activeTab === 'missed' && missed && (
          <div className="space-y-4">
            {missed.no_service_links?.length > 0 && (
              <Card className="bg-[#12121a] border-[#1e1e2e]"><CardHeader className="pb-2"><CardTitle className="text-sm text-red-400">Sessions Without Service Links ({missed.no_service_links.length})</CardTitle></CardHeader>
                <CardContent className="p-0"><div className="divide-y divide-[#1e1e2e]">
                  {missed.no_service_links.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#1a1a25] cursor-pointer" onClick={() => navigate(`/admin/group-sessions/${s.session_id}`)} data-testid={`missed-session-${i}`}>
                      <Badge className="bg-red-900/50 text-red-300 text-[10px]">HIGH</Badge>
                      <div className="flex-1"><p className="text-sm text-slate-200">{s.session_code} — {s.title}</p><p className="text-[10px] text-slate-600">{s.participant_count} participants, no service links</p></div>
                      <p className="text-[10px] text-blue-400">{s.suggestion}</p><ChevronRight className="h-4 w-4 text-slate-600" />
                    </div>
                  ))}
                </div></CardContent>
              </Card>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="bg-[#12121a] border-[#1e1e2e]"><CardContent className="p-4"><p className="text-xs text-slate-500">Ready Not Invoiced</p><p className="text-2xl font-bold text-amber-400 mt-1">{missed.ready_not_invoiced}</p><p className="text-[10px] text-slate-600">Links ready but missing invoice refs</p></CardContent></Card>
              <Card className="bg-[#12121a] border-[#1e1e2e]"><CardContent className="p-4"><p className="text-xs text-slate-500">Pending Review</p><p className="text-2xl font-bold text-yellow-400 mt-1">{missed.pending_review}</p><p className="text-[10px] text-slate-600">Links awaiting review</p></CardContent></Card>
              <Card className="bg-[#12121a] border-[#1e1e2e]"><CardContent className="p-4"><p className="text-xs text-slate-500">No Staff (Can't Bill)</p><p className="text-2xl font-bold text-red-400 mt-1">{missed.no_staff_sessions?.length || 0}</p><p className="text-[10px] text-slate-600">Sessions without staff assignment</p></CardContent></Card>
            </div>
          </div>
        )}

        {activeTab === 'anomalies' && anomalies && (
          <Card className="bg-[#12121a] border-[#1e1e2e]">
            <CardHeader className="pb-3"><CardTitle className="text-base text-slate-300">Billing Anomalies ({anomalies.total})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {anomalies.anomalies?.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">No anomalies detected</div>
              ) : (
                <div className="divide-y divide-[#1e1e2e]">
                  {anomalies.anomalies.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`anomaly-${i}`}>
                      <Badge className={`${SEVERITY_BG[a.severity]} text-[10px] shrink-0`}>{a.severity}</Badge>
                      <div className="flex-1"><p className="text-sm text-slate-200">{a.type.replace(/_/g, ' ')}</p><p className="text-[10px] text-slate-500">{a.detail}</p></div>
                      <p className="text-[10px] text-blue-400 max-w-[200px] text-right">{a.suggestion}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
